import { useEffect, useState } from 'react';

import { differenceInMinutes } from 'date-fns';
import { useIntl } from 'react-intl';

import { IOrderStatusDetail } from 'components/order-status/types';
import { getServiceModeHeaderText, orderInProgress } from 'components/order-status/utils';
import { deliveryCompleteTimeout, postOrderTimeout } from 'components/top-service-mode/utils';
import { DeliveryStatus, RbiOrderStatus, useGetUserOrdersQuery } from 'generated/rbi-graphql';
import { IImageFragment } from 'generated/sanity-graphql';
import useOrderNumber from 'hooks/use-order-number';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderStatus, ServiceMode, useOrderContext } from 'state/order';
import { useReversedUIContext } from 'state/reversed-ui';
import { useServiceModeContext } from 'state/service-mode';

const DELIVERY_COMPLETED = [
  DeliveryStatus.ORDER_DROPPED_OFF,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_CANCELLED,
];

const NOTIFICATION_EXPIRATION_IN_MINUTES = 120;
const ORDER_PROGRESS_POLLING_DURATION_IN_MS = 10000;
const DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES = 10;
const PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES = 60;

const isPollingForDelivery = ({
  hasDelivery,
  isDeliveryOrderCompleted,
  deliveryStatus,
}: {
  hasDelivery: boolean;
  isDeliveryOrderCompleted: boolean;
  deliveryStatus: DeliveryStatus | undefined;
}) =>
  hasDelivery &&
  !isDeliveryOrderCompleted &&
  (!deliveryStatus || !DELIVERY_COMPLETED.includes(deliveryStatus));

export const useOrderStatusDetail = (): IOrderStatusDetail | null => {
  const { formatMessage } = useIntl();
  const fireOrderAhead = useFlag(LaunchDarklyFlag.FIRE_ORDER_AHEAD);
  const { isAuthenticated } = useAuthContext();
  const { curbsidePickupOrderId, curbsidePickupOrderTimePlaced, serverOrder } = useOrderContext();
  const { isCurbside } = useServiceModeContext();
  const [hasSeenCancelModal, setHasSeenCancelModal] = useState(false);
  const [shouldDisplayNotification, setShouldDisplayNotification] = useState<boolean>(true);
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [currentDeliveryStatus, setCurrentDeliveryStatus] = useState<DeliveryStatus>();
  const { reversedUI } = useReversedUIContext();

  // start polling for recent user orders
  const { data, startPolling, stopPolling, refetch } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
    skip: !isAuthenticated,
  });

  const isPreConfirmCurbside = !!(
    curbsidePickupOrderId &&
    postOrderTimeout(curbsidePickupOrderTimePlaced, PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES)
  );

  // Upon order confirmation, data from `useGetUseOrdersQuery` is not updated with newly placed order
  // Force a refetch to update query with new confirm order instead of previous order
  const isServerOrderSuccessful: boolean = serverOrder?.status === OrderStatus.INSERT_SUCCESSFUL;
  const isCurbsideOrderCompleted = isCurbside && isServerOrderSuccessful;
  const order = data?.userOrders?.orders?.[0];
  // @ts-ignore
  const orderNumber = useOrderNumber(order);

  useEffect(() => {
    if (isCurbsideOrderCompleted) {
      refetch();
    }
  }, [isCurbsideOrderCompleted, refetch]);

  useEffect(() => {
    // Update notification for new orders being placed
    if (order && order.rbiOrderId !== currentOrderId) {
      setShouldDisplayNotification(true);
      setCurrentOrderId(order.rbiOrderId);
    }
  }, [currentOrderId, order, isCurbside]);

  useEffect(() => {
    // Update notification for delivery status changes
    if (order?.delivery?.status && order?.delivery.status !== currentDeliveryStatus) {
      setShouldDisplayNotification(true);
      setCurrentDeliveryStatus(order?.delivery?.status);
    }
  }, [currentDeliveryStatus, order]);

  useEffect(() => {
    // reactivate shouldDisplayNotification after closing first Curbside notification
    if (isServerOrderSuccessful) {
      setShouldDisplayNotification(true);
    }
  }, [isServerOrderSuccessful]);

  const recentOrder =
    isCurbsideOrderCompleted && serverOrder
      ? { ...serverOrder, updatedAt: serverOrder?.createdAt || '' }
      : data?.userOrders?.orders?.[0];

  if (!isAuthenticated || recentOrder?.status === RbiOrderStatus.REFUND_SUCCESSFUL) {
    // Stop polling for recent user orders
    stopPolling();
    return null;
  }

  const isDeliveryOrderCompleted =
    !!recentOrder?.delivery &&
    DELIVERY_COMPLETED.includes(recentOrder.delivery.status) &&
    (hasSeenCancelModal ||
      deliveryCompleteTimeout(recentOrder?.updatedAt, DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES));

  if (
    isPollingForDelivery({
      hasDelivery: !!recentOrder?.delivery,
      isDeliveryOrderCompleted,
      deliveryStatus: recentOrder?.delivery?.status,
    })
  ) {
    if (hasSeenCancelModal) {
      setHasSeenCancelModal(false);
    }
    startPolling(ORDER_PROGRESS_POLLING_DURATION_IN_MS);
  }

  const inProgress = orderInProgress({
    recentOrder,
    fireOrderAhead,
    isPreConfirmCurbside,
    isDeliveryOrderCompleted,
    isCurbside,
    reversedUI,
    formatMessage,
  });

  const notificationIsExpired =
    recentOrder?.updatedAt &&
    Math.abs(differenceInMinutes(new Date(), new Date(recentOrder.updatedAt))) >
      NOTIFICATION_EXPIRATION_IN_MINUTES;

  if (
    inProgress &&
    shouldDisplayNotification &&
    order &&
    order?.cart?.cartEntries &&
    !notificationIsExpired
  ) {
    const { details, icon } = inProgress;
    const totalItems =
      order?.cart?.cartEntries?.reduce(
        (total, cartEntry) => total + (cartEntry.quantity ?? 1),
        0
      ) ||
      order?.cart?.cartEntries?.length ||
      0;

    return {
      orderNumber,
      icon,
      deliveryStatus: recentOrder?.delivery?.status,
      heading: getServiceModeHeaderText(
        formatMessage,
        ServiceMode[order?.cart?.serviceMode ?? ServiceMode.DRIVE_THRU]
      ),
      details,
      image: (order?.cart?.cartEntries[0].image as IImageFragment) ?? undefined,
      name: order?.cart?.cartEntries[0].name ?? '',
      totalItems,
      orderId: isPreConfirmCurbside ? curbsidePickupOrderId : currentOrderId,
    };
  }

  return null;
};
